/**
 * Authentication utility functions for JWT token handling.
 * These utilities are used for UX decisions only - security is enforced on the backend.
 */

/**
 * Decodes a JWT token and returns the payload without verification.
 * This is safe for frontend use as the backend validates signatures.
 * Frontend only uses this for UX decisions, not security.
 * 
 * @param token - The JWT token string
 * @returns The decoded payload object or null if decoding fails
 */
export function decodeJwt(token: string): any {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

/**
 * Checks if the current user is an admin by examining the JWT token.
 * Returns false if token is invalid or isAdmin claim is not "true".
 * 
 * @param token - The JWT token string or null
 * @returns true if the user is an admin, false otherwise
 */
export function isAdminUser(token: string | null): boolean {
  if (!token) {
    return false;
  }
  
  const payload = decodeJwt(token);
  return payload?.isAdmin === 'true';
}

/**
 * Gets all claims from a JWT token for debugging purposes.
 * 
 * @param token - The JWT token string or null
 * @returns The claims object or null if token is invalid
 */
export function getTokenClaims(token: string | null): Record<string, any> | null {
  if (!token) {
    return null;
  }
  
  return decodeJwt(token);
}
